import { useEffect, useState } from "react";
import { SectionCard } from "../../components/SectionCard";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { api } from "../../api";
import type { BlogBlock } from "../../types";
import { navigate, usePathname } from "../../lib/router";
import { fixImageUrl } from "../../lib/utils";

export default function BlogEditorPage() {
  const path = usePathname();
  const isEditing = path !== "/app/blog/new";
  const slug = isEditing ? path.split("/")[3] : "";
  const [loading, setLoading] = useState(isEditing);
  const [form, setForm] = useState({
    title: "",
    subtitle: "",
    meta_title: "",
    meta_description: "",
    meta_site_name: "",
    meta_image: "",
    meta_image_width: "",
    meta_image_height: "",
    meta_image_type: "",
  });
  const [blocks, setBlocks] = useState<BlogBlock[]>([]);
  const [rawHtml, setRawHtml] = useState("");

  useEffect(() => {
    if (isEditing) {
      (async () => {
        const res = await api.getBlogPost(slug);
        if (res.ok) {
          const p = res.data;
          setForm({
            title: p.title,
            subtitle: p.subtitle || "",
            meta_title: p.meta_title || "",
            meta_description: p.meta_description || "",
            meta_site_name: p.meta_site_name || "",
            meta_image: p.meta_image || "",
            meta_image_width: p.meta_image_width ? String(p.meta_image_width) : "",
            meta_image_height: p.meta_image_height ? String(p.meta_image_height) : "",
            meta_image_type: p.meta_image_type || "",
          });
          setBlocks(p.content);
        }
        setLoading(false);
      })();
    }
  }, [isEditing, slug]);

  const importHtml = () => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rawHtml, "text/html");
    const newForm = { ...form };
    const h1 = doc.querySelector("h1");
    if (h1) newForm.title = h1.textContent || "";
    const h2 = doc.querySelector("h2");
    if (h2) newForm.subtitle = h2.textContent || "";
    const metaTitle = doc.querySelector('meta[property="og:title"]') || doc.querySelector("title");
    if (metaTitle) newForm.meta_title = metaTitle.getAttribute("content") || metaTitle.textContent || "";
    const metaDesc = doc.querySelector('meta[name="description"]') || doc.querySelector('meta[property="og:description"]');
    if (metaDesc) newForm.meta_description = metaDesc.getAttribute("content") || "";
    const siteName = doc.querySelector('meta[property="og:site_name"]');
    if (siteName) newForm.meta_site_name = siteName.getAttribute("content") || "";
    const image = doc.querySelector('meta[property="og:image"]');
    if (image) newForm.meta_image = image.getAttribute("content") || "";
    const newBlocks: BlogBlock[] = [];
    const walk = (node: Element) => {
      for (const child of Array.from(node.children)) {
        switch (child.tagName) {
          case "H1":
          case "H2":
            break;
          case "H3":
          case "H4":
          case "H5":
          case "H6":
            newBlocks.push({ type: "heading", level: Number(child.tagName[1]), text: child.textContent || "" });
            break;
          case "P":
            newBlocks.push({ type: "text", text: child.innerHTML });
            break;
          case "IMG":
            newBlocks.push({ type: "image", url: child.getAttribute("src") || "", alt: child.getAttribute("alt") || "" });
            break;
          case "OL":
          case "UL":
          case "LI":
          case "SECTION":
          case "HEADER":
            walk(child);
            break;
          default:
            walk(child);
        }
      }
    };
    if (doc.body) walk(doc.body);
    setForm(newForm);
    setBlocks(newBlocks);
  };

  const save = async () => {
    const payload = {
      title: form.title,
      subtitle: form.subtitle || null,
      blocks,
      meta_title: form.meta_title || null,
      meta_description: form.meta_description || null,
      meta_site_name: form.meta_site_name || null,
      meta_image: form.meta_image || null,
      meta_image_width: form.meta_image_width ? Number(form.meta_image_width) : null,
      meta_image_height: form.meta_image_height ? Number(form.meta_image_height) : null,
      meta_image_type: form.meta_image_type || null,
    };
    if (isEditing) {
      await api.updateBlogPost(slug, payload);
    } else {
      await api.createBlogPost(payload);
    }
    navigate("/app/blog");
  };

  if (loading) return <div className="max-w-screen-md mx-auto p-4">Carregando...</div>;

  return (
    <div className="max-w-screen-xl mx-auto p-4 grid md:grid-cols-2 gap-4">
      <SectionCard
        header={
          <h2 className="text-xl font-semibold">
            {isEditing ? "Editar post" : "Novo post"}
          </h2>
        }
        action={<Button onClick={save}>Salvar</Button>}
      >
        <div className="flex flex-col gap-2">
          <Input
            placeholder="Título"
            value={form.title}
            onChange={e => setForm({ ...form, title: e.target.value })}
          />
          <Input
            placeholder="Subtítulo"
            value={form.subtitle}
            onChange={e => setForm({ ...form, subtitle: e.target.value })}
          />
          <Input
            placeholder="Meta title"
            value={form.meta_title}
            onChange={e => setForm({ ...form, meta_title: e.target.value })}
          />
          <Textarea
            placeholder="Meta description"
            value={form.meta_description}
            onChange={e => setForm({ ...form, meta_description: e.target.value })}
          />
          <Input
            placeholder="Site name"
            value={form.meta_site_name}
            onChange={e => setForm({ ...form, meta_site_name: e.target.value })}
          />
          <Input
            placeholder="Imagem (URL)"
            value={form.meta_image}
            onChange={e => setForm({ ...form, meta_image: e.target.value })}
          />
          <div className="grid grid-cols-3 gap-2">
            <Input
              placeholder="Largura"
              value={form.meta_image_width}
              onChange={e => setForm({ ...form, meta_image_width: e.target.value })}
            />
            <Input
              placeholder="Altura"
              value={form.meta_image_height}
              onChange={e => setForm({ ...form, meta_image_height: e.target.value })}
            />
            <Input
              placeholder="Tipo"
              value={form.meta_image_type}
              onChange={e => setForm({ ...form, meta_image_type: e.target.value })}
            />
          </div>
          <Textarea
            placeholder="Cole HTML do post"
            value={rawHtml}
            onChange={e => setRawHtml(e.target.value)}
          />
          <Button type="button" variant="outline" onClick={importHtml}>
            Importar HTML
          </Button>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setBlocks([...blocks, { type: "text", text: "" }])}>
              + Texto
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBlocks([...blocks, { type: "image", url: "", alt: "" }])}
            >
              + Imagem
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setBlocks([...blocks, { type: "heading", level: 3, text: "" }])}
            >
              + Título
            </Button>
          </div>
          <div className="flex flex-col gap-4">
            {blocks.map((block, idx) => (
              <div key={idx} className="flex flex-col gap-2 border p-2 rounded-md">
                {block.type === "text" ? (
                  <Textarea
                    placeholder="Texto"
                    value={block.text}
                    onChange={e => {
                      const copy = [...blocks];
                      copy[idx] = { ...block, text: e.target.value };
                      setBlocks(copy);
                    }}
                  />
                ) : block.type === "image" ? (
                  <div className="flex flex-col gap-2">
                    <Input
                      placeholder="URL da imagem"
                      value={block.url}
                      onChange={e => {
                        const copy = [...blocks];
                        copy[idx] = { ...block, url: e.target.value } as BlogBlock;
                        setBlocks(copy);
                      }}
                    />
                    <Input
                      placeholder="Texto alternativo"
                      value={block.alt || ""}
                      onChange={e => {
                        const copy = [...blocks];
                        copy[idx] = { ...block, alt: e.target.value } as BlogBlock;
                        setBlocks(copy);
                      }}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    <Input
                      placeholder="Nível (1-6)"
                      value={String(block.level)}
                      onChange={e => {
                        const copy = [...blocks];
                        copy[idx] = {
                          ...block,
                          level: Number(e.target.value) || 1,
                        } as BlogBlock;
                        setBlocks(copy);
                      }}
                    />
                    <Input
                      placeholder="Texto do título"
                      value={block.text}
                      onChange={e => {
                        const copy = [...blocks];
                        copy[idx] = { ...block, text: e.target.value } as BlogBlock;
                        setBlocks(copy);
                      }}
                    />
                  </div>
                )}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    disabled={idx === 0}
                    onClick={() => {
                      const copy = [...blocks];
                      const [item] = copy.splice(idx, 1);
                      copy.splice(idx - 1, 0, item);
                      setBlocks(copy);
                    }}
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    disabled={idx === blocks.length - 1}
                    onClick={() => {
                      const copy = [...blocks];
                      const [item] = copy.splice(idx, 1);
                      copy.splice(idx + 1, 0, item);
                      setBlocks(copy);
                    }}
                  >
                    ↓
                  </Button>
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setBlocks(blocks.filter((_, i) => i !== idx))}
                  >
                    Remover
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>
      <SectionCard header={<h2 className="text-xl font-semibold">Pré-visualização</h2>}>
        <div className="flex flex-col">
          <h1 className="text-2xl font-bold">{form.title}</h1>
          {form.subtitle && <h2 className="text-lg text-neutral-700 mb-4">{form.subtitle}</h2>}
          {blocks.map((block, idx) => {
            if (block.type === "text")
              return (
                <p
                  key={idx}
                  className="mb-4"
                  dangerouslySetInnerHTML={{ __html: block.text }}
                />
              );
            if (block.type === "image")
              return (
                <img
                  key={idx}
                  src={fixImageUrl(block.url)}
                  alt={block.alt || ""}
                  className="my-4"
                />
              );
            const Tag = `h${block.level}` as keyof JSX.IntrinsicElements;
            return (
              <Tag key={idx} className="mt-4 mb-2 font-semibold">
                {block.text}
              </Tag>
            );
          })}
        </div>
      </SectionCard>
    </div>
  );
}
